/**
 * ComfyGallery.jsx
 * Gallery component for displaying ComfyUI output media (images, videos, audio).
 *
 * Uses the same CollectionImage/Video/Audio components as Generate.jsx,
 * rendering media directly from raw file paths. Items come from the SQLite
 * database via comfyOutputService → comfy:getMedia IPC handler, which maps
 * fields to the camelCase props these components expect.
 *
 * Pagination is server-driven (totalCount/hasMore) with infinite scroll.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getMediaItems, deleteMedia } from '../services/comfyOutputService';
import Masonry from 'react-masonry-css';
import CollectionImage from './CollectionImage';
import Video from './Video';
import Audio from './Audio';
import ModelCanvas from './3DCanvas.jsx';
import { db } from '../db';
import { useCollectionContext } from '../context/CollectionContext';

const PAGE_SIZE = 40;

const ComfyGallery = ({
  category = 'all',
  sortMethod = 'newest',
  columnCount = 4,
  onCountUpdate
}) => {
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const observerRef = useRef(null);
  const lastItemRef = useRef(null);

  const collectionContext = useCollectionContext();

  // ── Data Loading ──────────────────────────────────────────────────────────

  const loadInitialMedia = useCallback(async () => {
    setIsInitialLoad(true);
    setLoading(true);
    setMediaItems([]);
    setCurrentPage(1);

    try {
      const response = await getMediaItems({ page: 1, pageSize: PAGE_SIZE, category, sortMethod });

      if (response.success && response.data) {
        setMediaItems(response.data.items);
        setHasMore(response.data.hasMore);
        if (onCountUpdate) onCountUpdate(response.data.totalCount);
      } else {
        setMediaItems([]);
        setHasMore(false);
        if (onCountUpdate) onCountUpdate(0);
      }
    } catch (error) {
      console.error('[ComfyGallery] Error loading media:', error);
      if (onCountUpdate) onCountUpdate(0);
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  }, [category, sortMethod, onCountUpdate]);

  const loadMoreMedia = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);

    try {
      const nextPage = currentPage + 1;
      const response = await getMediaItems({ page: nextPage, pageSize: PAGE_SIZE, category, sortMethod });

      if (response.success && response.data && response.data.items.length > 0) {
        const existingHashes = new Set(mediaItems.map(i => i.hash));
        const newItems = response.data.items.filter(i => !existingHashes.has(i.hash));

        if (newItems.length > 0) {
          setMediaItems(prev => [...prev, ...newItems]);
          setCurrentPage(nextPage);
        }
        setHasMore(response.data.hasMore);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('[ComfyGallery] Error loading more media:', error);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, currentPage, category, sortMethod, mediaItems]);

  useEffect(() => {
    loadInitialMedia();
  }, [category, sortMethod]);

  // Infinite scroll observer
  useEffect(() => {
    if (loading || !hasMore) return;

    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loading) {
        loadMoreMedia();
      }
    }, { root: null, rootMargin: '200px', threshold: 0.1 });

    if (lastItemRef.current) {
      observerRef.current.observe(lastItemRef.current);
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [loading, hasMore, mediaItems]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleDelete = async (hash, event) => {
    if (event) event.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const result = await deleteMedia(hash);
      if (result.success) {
        setMediaItems(prev => prev.filter(item => item.hash !== hash));
      }
    } catch (error) {
      console.error('[ComfyGallery] Error deleting media:', error);
    }
  };

  const refreshGallery = () => {
    loadInitialMedia();
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const breakpointCols = useMemo(() => ({
    default: columnCount,
    2560: Math.min(columnCount, 6),
    1920: Math.min(columnCount, 6),
    1536: Math.min(columnCount, 5),
    1280: Math.min(columnCount, 4),
    1024: Math.min(columnCount, 3),
    768:  Math.min(columnCount, 3),
    640:  Math.min(columnCount, 2),
  }), [columnCount]);

  return (
    <div className="comfy-gallery w-full">
      {isInitialLoad ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow"></div>
        </div>
      ) : mediaItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-lg text-gray-300 mb-4">No media items found</p>
        </div>
      ) : (
        <Masonry
          breakpointCols={breakpointCols}
          className="masonry-grid p-4 [contain:content]"
          columnClassName="masonry-grid_column"
        >
          {mediaItems.map((item, index) => (
            <div
              key={item.hash}
              ref={index === mediaItems.length - 1 ? lastItemRef : null}
            >
              {item.dataType === 'video' ? (
                <Video
                  {...item}
                  isMyCollection={false}
                  dbTable={db.images}
                  tagArray={collectionContext.tags}
                  refreshTags={() => {}}
                  refreshImages={refreshGallery}
                  onOpen={() => {}}
                  viewFailedImage={false}
                />
              ) : item.dataType === 'audio' ? (
                <Audio
                  {...item}
                  name={item.prompt || item.name || "Audio File"}
                  isMyCollection={false}
                  dbTable={db.images}
                  tagArray={collectionContext.tags}
                  refreshTags={() => {}}
                  refreshImages={refreshGallery}
                  onOpen={() => {}}
                  viewFailedImage={false}
                />
              ) : item.dataType === '3d' ? (
                <ModelCanvas
                  {...item}
                  onOpen={() => {}}
                  viewFailedImage={false}
                />
              ) : (
                <CollectionImage
                  {...item}
                  isMyCollection={false}
                  dbTable={db.images}
                  tagArray={collectionContext.tags}
                  refreshTags={() => {}}
                  refreshImages={refreshGallery}
                  onOpen={() => {}}
                  viewFailedImage={false}
                />
              )}
            </div>
          ))}
        </Masonry>
      )}

      {loading && !isInitialLoad && (
        <div className="flex justify-center items-center py-4 mt-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow"></div>
        </div>
      )}
    </div>
  );
};

export default ComfyGallery;
